import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button, Card, Container, Dropdown, Table } from 'react-bootstrap';
import { Icon } from '@lomarengas/design-system';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  AccommodationGroupQueryFilters,
  getAllAccommodationGroupsAsync,
  selectAccommodationGroups,
  deleteAccommodationGroupAsync,
  selectAccommodationGroupsPagination,
  selectAccommodationGroupsLoadingStatus,
  addAccommodationGroupAsync,
  AddAccommodationGroupDetails,
} from './accommodationGroupsSlice';
import { LOADING_STATUS } from '../../common/constants';
import {
  DataTable,
  DataTableItem,
  PaginationComponent,
  Loader,
  SectionHeader,
  DataTableItemAction,
} from '../../common/components';
import { InitialPaginationParameters } from '../../common/types/pagination.types';
import { saveAccommodationAsync } from './accommodationsSlice';

function AccommodationGroups() {
  const history = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const accommodationGroups = useAppSelector(selectAccommodationGroups);
  const accommodationGroupsPagination = useAppSelector(
    selectAccommodationGroupsPagination,
  );
  const [deletesubmitted, setDeleteSubmitted] = useState(false);
  const accommodationGroupsLoadingStatus = useAppSelector(
    selectAccommodationGroupsLoadingStatus,
  );

  const [filtering, setFiltering] = useState<AccommodationGroupQueryFilters>({
    PageSize: InitialPaginationParameters.PageSize,
    PageNumber: InitialPaginationParameters.PageNumber,
    isDeleted: false,
  });

  // gets translated item based on keyId and locale
  // this is used in html to fetch proper translation of text
  function getItem(locale, keyId, item) {
    if (accommodationGroupsLoadingStatus === LOADING_STATUS.IDLE) {
      const found = item.translations.filter(
        a => a.locale === locale && a.keyId === keyId,
      );
      if (found && found[0] && found[0].text) {
        return found[0].text;
      }
    }
    return '';
  }

  const addAccommodationGroup = () => {
    dispatch(
      addAccommodationGroupAsync({
        id: 'new',
        isDeleted: false,
        translations: [],
        groupLetter: '',
        hideFromLomarengasSite: false,
      } as AddAccommodationGroupDetails),
    ).then(res => {
      history(`/accommodations/accommodationgroups/${res.payload}`);
    });
  };

  const [accommodationGroupTableItems, setAccommodationGroupTableItems] =
    useState<DataTableItem[]>([]);

  const [accommodationGroupActiontems, setAccommodationGroupActiontems] =
    useState<DataTableItemAction[]>([]);
  function handleDeleteGroup(data) {
    dispatch(deleteAccommodationGroupAsync(data));
    setDeleteSubmitted(!deletesubmitted);
    setAccommodationGroupTableItems([]);
    dispatch(getAllAccommodationGroupsAsync(filtering));
  }

  useEffect(() => {
    if (accommodationGroups && accommodationGroups.length > 0) {
      const formattedAccommodationGroupsList: DataTableItem[] = [];
      const formattedAccommodationGroupsActionsList: DataTableItemAction[] = [];
      accommodationGroups?.forEach(item => {
        formattedAccommodationGroupsList.push({
          ItemId: item.id,
          Items: [item.name],
          Url: `/accommodations/accommodationgroups/${item.id}`,
        });
      });
      formattedAccommodationGroupsActionsList.push({
        ButtonAction: id => handleDeleteGroup(id),
        ButtonText: 'Poista',
      });
      setAccommodationGroupTableItems(formattedAccommodationGroupsList);
      setAccommodationGroupActiontems(formattedAccommodationGroupsActionsList);
    }
  }, [accommodationGroups]);

  useEffect(() => {
    if (accommodationGroupTableItems.length > 0) {
      setAccommodationGroupTableItems(accommodationGroupTableItems);
    }
  }, [accommodationGroupTableItems]);
  // fills in showDeleted criteria in filtering object
  // stored value is readOnly, so we need to have temp
  // object and fill in data and then store criteria
  function showDeleted(value) {
    const isDeletedtemp = true;
    const query = { ...filtering, isDeleted: isDeletedtemp };
    setFiltering({ ...filtering, isDeleted: isDeletedtemp });
    dispatch(getAllAccommodationGroupsAsync(query));
  }

  // calls actual search, which calls internal getAllAccommodationGroupsAsync
  // and that is then routed to backend
  const doSearch = () => {
    const isDeletedtemp = false;
    const query = { ...filtering, isDeleted: isDeletedtemp };
    setFiltering({ ...filtering, isDeleted: isDeletedtemp });
    dispatch(getAllAccommodationGroupsAsync(query));
  };

  useEffect(() => {
    doSearch();
  }, []);

  const HandlePaginationClick = (Page: number) => {
    const paginatedFiltering = filtering;
    paginatedFiltering.PageNumber = Page;
    setFiltering(paginatedFiltering);
    dispatch(getAllAccommodationGroupsAsync(paginatedFiltering));
  };

  const AddNewButton: JSX.Element = (
    <Button variant="blue" className="ps-16" onClick={addAccommodationGroup}>
      <Icon CssClass="me-8" AriaLabel="Uusi" Name="plus" />
      Lisää uusi
    </Button>
  );

  // draws elements to browser
  return (
    <>
      <Helmet>
        <title>Majoitus Ryhmät - Haku</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Majoitukset ja Ryhmät']}
          Children={AddNewButton}
          Title="Majoitusryhmät"
        />

        <Loader
          IsLoading={
            accommodationGroupsLoadingStatus === LOADING_STATUS.LOADING
          }
        />
        {accommodationGroupsLoadingStatus === LOADING_STATUS.IDLE &&
          accommodationGroups !== null &&
          accommodationGroups.length > 0 && (
            <DataTable
              Header={['Majoitusryhmän nimi']}
              Items={accommodationGroupTableItems}
              DeleteItem={id => handleDeleteGroup(id)}
              LegendText="majoitusryhmää"
              TotalItems={accommodationGroupsPagination.totalCount}
            />
          )}

        {accommodationGroupsLoadingStatus === LOADING_STATUS.IDLE &&
          accommodationGroups != null &&
          accommodationGroups.length < 1 && <p>Ei majoitusryhmiä.</p>}
      </Container>
    </>
  );
}

export default AccommodationGroups;
